import { createStyles, IconButton, makeStyles, Menu, MenuItem, Theme } from '@material-ui/core';
import { MoreVert } from '@material-ui/icons';
import React, { forwardRef, MouseEvent, ReactNode, useImperativeHandle, useState } from 'react';

export type RowAction = {
  actionItem: ReactNode;
  onClick?: () => void;
  menuItemProps?: {
    disabled?: boolean;
  };
};

export type DataGridRowActionProps = {
  actions: RowAction[];
  disabled?: boolean;
};

export type DataGridRowActionHandlers = {
  closeMenu: () => void;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menuOpenRow: {
      backgroundColor: theme.palette.primary.light,
    },
  }),
);

export const DataGridRowAction = forwardRef<DataGridRowActionHandlers, DataGridRowActionProps>(
  ({ actions, disabled, ...props }: DataGridRowActionProps, ref): JSX.Element => {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [parentRowEl, setParentRowEl] = useState<null | HTMLElement>(null);

    // Exposing this to the parent to allow consumers to close this menu imperatively
    useImperativeHandle(
      ref,
      () => ({
        closeMenu: () => {
          parentRowEl?.classList?.remove(classes.menuOpenRow);
          setAnchorEl(null);
        },
      }),
      [parentRowEl, anchorEl, ref],
    );

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      // highlight row on data grid when menu is open
      if (event.nativeEvent && event.nativeEvent.composedPath) {
        const rowEl = event.nativeEvent.composedPath().find(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (elem: any) =>
            elem.className &&
            typeof elem.className === 'string' &&
            elem.className.includes('MuiTableRow-root MuiTableRow-hover'),
        );
        (rowEl as HTMLElement)?.classList?.add(classes.menuOpenRow);
        setParentRowEl(rowEl as HTMLElement);
      }
      setAnchorEl(event.currentTarget);
    };

    const handleClose = (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      parentRowEl?.classList?.remove(classes.menuOpenRow);
      setAnchorEl(null);
    };

    return (
      <>
        <IconButton size="small" onClick={handleClick} data-testid="row-menu-button" disabled={disabled}>
          <MoreVert />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose} {...props}>
          {actions.map((rowAction, index) => {
            const { actionItem, onClick, menuItemProps } = rowAction;
            return (
              <MenuItem
                key={index}
                onClick={(event: MouseEvent<HTMLLIElement>) => {
                  event.stopPropagation();
                  event.preventDefault();
                  onClick?.();
                }}
                data-testid="row-menu-item"
                {...menuItemProps}
              >
                {actionItem}
              </MenuItem>
            );
          })}
        </Menu>
      </>
    );
  },
);

DataGridRowAction.displayName = 'DataGridrowAction';
